import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import styles from './TaskModal.module.scss';
import api from '../../services/api';

Modal.setAppElement('#root');

const TASK_KIND_OPTIONS = [
    { value: 'common', label: 'Обычная задача' },
    { value: 'service_improvement', label: 'Доработка сервиса' },
];

function resolveTaskKindOption(taskKind) {
    return TASK_KIND_OPTIONS.find((option) => option.value === taskKind) || TASK_KIND_OPTIONS[0];
}

const EditTaskModal = ({ isOpen, onRequestClose, task, onSaveTask, canUseInternalMessages = false }) => {
    const [title, setTitle] = useState(task?.title || '');
    const [taskKind, setTaskKind] = useState(resolveTaskKindOption(task?.taskKind));
    const [author, setAuthor] = useState(task?.author ? { value: task.author.id, label: task.author.label } : null);
    const [assignee, setAssignee] = useState(task?.assignee ? { value: task.assignee.id, label: task.assignee.label } : null);
    const [observers, setObservers] = useState(
        Array.isArray(task?.observers)
            ? task.observers.map((observer) => ({ value: observer.id, label: observer.label }))
            : []
    );
    const [description, setDescription] = useState(() => {
        if (task?.body && typeof task.body === 'object') return JSON.stringify(task.body, null, 2);
        return task?.description || '';
    });
    const [priority, setPriority] = useState(task?.priority ? { value: task.priority, label: task.priority } : null);
    const [stage, setStage] = useState(task?.status ? { value: task.status, label: task.status } : null);
    const [internalComment, setInternalComment] = useState(task?.localComment || '');
    const [partnerLocalComment, setPartnerLocalComment] = useState(task?.partnerLocalComment || '');
    const [terminalNumber, setTerminalNumber] = useState(task?.terminalNumber || '');
    const [estimatedResolutionDate, setEstimatedResolutionDate] = useState(task?.estimatedResolutionDate || '');

    const [usersOptionsAuthors, setUsersOptionsAuthors] = useState([]);
    const [usersOptionsObserver, setUsersOptionsObserver] = useState([]);
    const [usersOptionsPerformers, setUsersOptionsPerformers] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setTitle(task?.title || '');
        setTaskKind(resolveTaskKindOption(task?.taskKind));
        setAuthor(task?.author ? { value: task.author.id, label: task.author.label } : null);
        setAssignee(task?.assignee ? { value: task.assignee.id, label: task.assignee.label } : null);
        setObservers(
            Array.isArray(task?.observers)
                ? task.observers.map((observer) => ({ value: observer.id, label: observer.label }))
                : []
        );
        if (task?.body && typeof task.body === 'object') {
            setDescription(JSON.stringify(task.body, null, 2));
        } else {
            setDescription(task?.description || '');
        }
        setPriority(task?.priority ? { value: task.priority, label: task.priority } : null);
        setStage(task?.status ? { value: task.status, label: task.status } : null);
        setInternalComment(task?.localComment || '');
        setPartnerLocalComment(task?.partnerLocalComment || '');
        setTerminalNumber(task?.terminalNumber || '');
        setEstimatedResolutionDate(task?.estimatedResolutionDate || '');
        setErrors({});
    }, [task, isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const fetchTaskUsers = async () => {
            try {
                const response = await api.get('/api/usersTask');
                setUsersOptionsAuthors(Array.isArray(response.data?.Authors) ? response.data.Authors : []);
                setUsersOptionsObserver(Array.isArray(response.data?.Observer) ? response.data.Observer : []);
                setUsersOptionsPerformers(Array.isArray(response.data?.Performers) ? response.data.Performers : []);
            } catch (error) {
                console.error('Ошибка при загрузке списков пользователей:', error);
            }
        };

        fetchTaskUsers();
    }, [isOpen]);

    const priorityOptions = [
        { value: '4', label: 'Не важная' },
        { value: '2', label: 'Средняя' },
        { value: '3', label: 'Важная' },
        { value: '1', label: 'Критическая' },
    ];

    const stageOptions = useMemo(
        () => [
            { value: 'Свободно', label: 'Свободно' },
            { value: 'В работе', label: 'В работе' },
            { value: 'Требует внимание партнера', label: 'Требует внимание партнера' },
            { value: 'Закрыто', label: 'Закрыто' },
            { value: 'Отклонено', label: 'Отклонено' },
            { value: 'В архиве', label: 'В архиве' },
        ],
        []
    );

    const validateFields = () => {
        const nextErrors = {};

        if (!title.trim()) nextErrors.title = 'Название задачи обязательно';
        if (!author) nextErrors.author = 'Автор задачи обязателен';
        if (!assignee) nextErrors.assignee = 'Исполнитель задачи обязателен';
        if (!description.trim()) nextErrors.description = 'Описание задачи обязательно';
        if (!priority) nextErrors.priority = 'Важность задачи обязательна';
        if (!stage) nextErrors.stage = 'Этап задачи обязателен';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSaveTask = () => {
        if (!validateFields()) return;

        let parsedDescription = description;
        try {
            const parsed = JSON.parse(description);
            if (typeof parsed === 'object' && parsed !== null) {
                parsedDescription = parsed;
            }
        } catch {
            parsedDescription = description;
        }

        const updatedTask = {
            ...task,
            title,
            taskKind: taskKind?.value || 'common',
            author: author.value,
            assignee: assignee.value,
            observers: observers.map((observer) => observer.value),
            description: parsedDescription,
            priority: priority.value,
            stage: stage.value,
            partnerLocalComment,
            terminalNumber: terminalNumber.trim(),
            estimatedResolutionDate: estimatedResolutionDate || null,
        };

        if (canUseInternalMessages) {
            updatedTask.internalComment = internalComment;
        }

        onSaveTask(updatedTask);
        onRequestClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel="Редактирование задачи"
            className={styles.modalContent}
            overlayClassName="appModalOverlay"
        >
            <h2>Редактировать задачу</h2>

            <div className={styles.field}>
                <label>Название задачи</label>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Введите название задачи"
                    className={errors.title ? styles.errorInput : ''}
                />
                {errors.title && <span className={styles.errorMessage}>{errors.title}</span>}
            </div>

            <div className={styles.field}>
                <label>Тип задачи</label>
                <Select
                    options={TASK_KIND_OPTIONS}
                    value={taskKind}
                    onChange={(selected) => setTaskKind(selected || TASK_KIND_OPTIONS[0])}
                    placeholder="Выберите тип задачи"
                    isSearchable={false}
                />
            </div>

            <div className={styles.field}>
                <label>Автор задачи</label>
                <Select
                    options={usersOptionsAuthors}
                    value={author}
                    onChange={setAuthor}
                    placeholder="Выберите автора"
                    isSearchable
                    className={errors.author ? styles.errorSelect : ''}
                />
                {errors.author && <span className={styles.errorMessage}>{errors.author}</span>}
            </div>

            <div className={styles.field}>
                <label>Исполнитель задачи</label>
                <Select
                    options={usersOptionsPerformers}
                    value={assignee}
                    onChange={setAssignee}
                    placeholder="Выберите исполнителя"
                    isSearchable
                    className={errors.assignee ? styles.errorSelect : ''}
                />
                {errors.assignee && <span className={styles.errorMessage}>{errors.assignee}</span>}
            </div>

            <div className={styles.field}>
                <label>Наблюдатели</label>
                <Select
                    options={usersOptionsObserver}
                    value={observers}
                    onChange={(selected) => setObservers(selected || [])}
                    placeholder="Выберите наблюдателей"
                    isMulti
                    isSearchable
                />
            </div>

            <div className={styles.field}>
                <label>Номер терминала</label>
                <input
                    type="text"
                    value={terminalNumber}
                    onChange={(e) => setTerminalNumber(e.target.value)}
                    placeholder="Например: T-1023"
                />
            </div>

            <div className={styles.field}>
                <label>Примерная дата решения</label>
                <input
                    type="date"
                    value={estimatedResolutionDate}
                    onChange={(e) => setEstimatedResolutionDate(e.target.value)}
                />
            </div>

            <div className={styles.field}>
                <label>Описание задачи</label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Введите описание задачи"
                    rows={6}
                    className={errors.description ? styles.errorTextarea : ''}
                />
                {errors.description && <span className={styles.errorMessage}>{errors.description}</span>}
            </div>

            <div className={styles.field}>
                <label>Важность задачи</label>
                <Select
                    options={priorityOptions}
                    value={priority}
                    onChange={setPriority}
                    placeholder="Выберите важность"
                    className={errors.priority ? styles.errorSelect : ''}
                />
                {errors.priority && <span className={styles.errorMessage}>{errors.priority}</span>}
            </div>

            <div className={styles.field}>
                <label>Этап задачи</label>
                <Select
                    options={stageOptions}
                    value={stage}
                    onChange={setStage}
                    placeholder="Выберите этап"
                    className={errors.stage ? styles.errorSelect : ''}
                />
                {errors.stage && <span className={styles.errorMessage}>{errors.stage}</span>}
            </div>

            <div className={styles.field}>
                <label>Комментарий по партнёру</label>
                <textarea
                    value={partnerLocalComment}
                    onChange={(e) => setPartnerLocalComment(e.target.value)}
                    placeholder="Введите внутренний комментарий по партнёру"
                    rows={4}
                />
            </div>

            {canUseInternalMessages ? (
                <div className={styles.field}>
                    <label>Внутренний комментарий</label>
                    <textarea
                        value={internalComment}
                        onChange={(e) => setInternalComment(e.target.value)}
                        placeholder="Введите внутренний комментарий"
                        rows={4}
                    />
                </div>
            ) : null}

            <div className={styles.buttonsContainer}>
                <button onClick={handleSaveTask}>Сохранить</button>
                <button onClick={onRequestClose}>Отмена</button>
            </div>
        </Modal>
    );
};

export default EditTaskModal;
